import { getStockDateInfo } from "./getStockDateInfo";
import { getDateFromStatusMessage } from "./getDateFromStatusMessage";
import {
  isFuturePreorder,
  isLongHorizonPreorder,
} from "./isLongHorizonPreorder";
import { ItemAvailability } from "schema-dts";

export type AddToCartButtonState = {
  disabled: boolean;
  label?: string;
  structured?: ItemAvailability;
  isLongHorizonPreorder?: boolean;
};

const AVAILABILITY = {
  awaitingStock: "awaitingStock",
  temporarilyOutOfStock: "temporarilyOutOfStock",
  inStock: "inStock",
  printOnDemand: "printOnDemand",
  unknown: "unknown",
} as const;

type Availability = (typeof AVAILABILITY)[keyof typeof AVAILABILITY];

const STATUS_MESSAGE_RULES = [
  { pattern: /^Forventet i salg\b/i, availability: AVAILABILITY.awaitingStock },
  {
    pattern: /^Midlertidig utsolgt\b/i,
    availability: AVAILABILITY.temporarilyOutOfStock,
  },
  {
    pattern: /^Produseres på bestilling\b/i,
    availability: AVAILABILITY.printOnDemand,
  },
  { pattern: /^Sendes umiddelbart\b/i, availability: AVAILABILITY.inStock },
  {
    pattern: /^Sendes fra oss i løpet av\b/i,
    availability: AVAILABILITY.inStock,
  },
] as const;

function getAvailabilityFromStatusMessage(
  statusMessage?: string,
): Availability {
  const message = (statusMessage ?? "").trim();
  if (!message) return AVAILABILITY.unknown;

  const match = STATUS_MESSAGE_RULES.find(({ pattern }) =>
    pattern.test(message),
  );
  return match?.availability ?? AVAILABILITY.unknown;
}

/**
 * Determines if purchase is allowed based on the date found in statusMessage:
 * - true  => date is today/past OR within next 30 days (inclusive)
 * - false => date exists but is outside allowed window
 * - null  => no parseable/valid date found
 */
function isPurchaseAllowedFromStatusDate(
  statusMessage?: string,
): boolean | null {
  const isoDate = getDateFromStatusMessage(statusMessage);
  if (!isoDate) return null;

  const stockDateInfo = getStockDateInfo(isoDate);
  if (!stockDateInfo.hasDate) return null;

  return stockDateInfo.isPastOrToday || stockDateInfo.isWithinNext30;
}

export function getAddToCartButtonState(
  canBeSold: boolean,
  statusMessage?: string,
  publicationDate?: string,
): AddToCartButtonState {
  if (!canBeSold) {
    return {
      disabled: true,
      label: "Ikke tilgjengelig",
      structured: "https://schema.org/OutOfStock",
    };
  }

  const availability = getAvailabilityFromStatusMessage(statusMessage);
  const isLongHorizonPreorderItem = isLongHorizonPreorder({
    publicationDate,
    statusMessage,
  });

  if (
    isFuturePreorder({ publicationDate }) ||
    (availability === AVAILABILITY.awaitingStock &&
      isFuturePreorder({ statusMessage }))
  ) {
    return {
      disabled: false,
      label: "Forhåndsbestill",
      structured: "https://schema.org/PreOrder",
      ...(isLongHorizonPreorderItem ? { isLongHorizonPreorder: true } : {}),
    };
  }

  if (
    availability === AVAILABILITY.inStock ||
    availability === AVAILABILITY.printOnDemand ||
    availability === AVAILABILITY.unknown
  ) {
    return { disabled: false, structured: "https://schema.org/InStock" };
  }

  if (availability === AVAILABILITY.awaitingStock) {
    const allowed = isPurchaseAllowedFromStatusDate(statusMessage);

    if (allowed === true) {
      return { disabled: false, structured: "https://schema.org/PreOrder" };
    }

    return {
      disabled: true,
      label: "Kommer snart",
      structured: "https://schema.org/PreOrder",
    };
  }

  if (availability === AVAILABILITY.temporarilyOutOfStock) {
    const allowed = isPurchaseAllowedFromStatusDate(statusMessage);
    const disabled = allowed !== true;

    return {
      disabled,
      label: disabled ? "Utsolgt" : undefined,
      structured: "https://schema.org/OutOfStock",
    };
  }

  return { disabled: false, structured: "https://schema.org/InStock" };
}
